import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  MapPin,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  X,
  Edit,
  Shield,
  Users,
  ArrowRight,
  Send,
  Star,
  User,
  Timer,
  TrendingUp,
  Repeat,
  Route,
  Weight,
  ShoppingBasket,
  Car,
  PlaneTakeoff,
  Users2,
  Trash
} from 'lucide-react';
import { useThemeColors } from '../theme/ThemeContext';
import { applicationsAPI } from '../services/api';
import toast from 'react-hot-toast';

const TripCardSleek = ({
  trip,
  showActions,
  isOwner,
  onCancel,
  onEdit,
  onApply,
  onDelete
}) => {
  const colors = useThemeColors();
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/trips/${trip.id}`);
  };

  const handleApply = async (e) => {
    e.stopPropagation();

    try {
      await applicationsAPI.createApplication(
        'trip',
        trip.id,
        `I would like to apply for this trip from ${trip.origin_city} to ${trip.destination_city}.`
      );

      toast.success('Application submitted successfully!');

      // Call parent onApply callback if provided
      if (onApply) {
        onApply(trip);
      }
    } catch (error) {
      console.error('Apply error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit application');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'posted':
        return { bg: colors.success[50], text: colors.success[700], border: colors.success[200] };
      case 'in_progress':
        return { bg: colors.primary[50], text: colors.primary[700], border: colors.primary[200] };
      case 'completed':
        return { bg: colors.neutral[50], text: colors.neutral[700], border: colors.neutral[200] };
      case 'cancelled':
        return { bg: colors.error[50], text: colors.error[700], border: colors.error[200] };
      default:
        return { bg: colors.neutral[50], text: colors.neutral[700], border: colors.neutral[200] };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'posted':
        return <Clock className="h-4 w-4" />;
      case 'in_progress':
        return <Package className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'cancelled':
        return <X className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };
  const canApply = trip.status === 'posted' && !showActions;
  const canEdit = isOwner && showActions && ['posted'].includes(trip.status);
  const canCancel = isOwner && showActions && ['posted'].includes(trip.status);
  const canDelete = isOwner && showActions && ['cancelled', 'expired'].includes(trip.status);

  const daysUntilDeparture = trip.departure_date
    ? Math.ceil((new Date(trip.departure_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const isDepartingSoon = daysUntilDeparture !== null && daysUntilDeparture <= 7;

  const formatValue = (value) => {
    if (!value) return 'N/A';
    const num = parseFloat(value);
    const formattedValue = new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
      maximumFractionDigits: 0
    }).format(num);
    return formattedValue;
  };

  const statusColors = getStatusColor(trip.status);

  return (
    <div
      className="group shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer border rounded-2xl"
      style={{
        backgroundColor: colors.card,
        borderColor: colors.border
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = colors.primary[300];
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = colors.border;
      }}
      onClick={handleCardClick}
    >
      {/* Content */}
      <div className="p-6">
        {/* Route */}
        <div className="mb-6">
          <div className=" items-center justify-center">
            <div className="flex-1">
              <div className="flex items-center" style={{ color: colors.text.secondary }}>
                <MapPin className="h-4 w-4 mr-2" style={{ color: colors.success[500] }} />
                <div>
                  <p className="text-xs uppercase tracking-wide" style={{ color: colors.text.tertiary }}>From</p>
                  <p className="font-semibold truncate" style={{ color: colors.text.primary }}>{trip.origin_city}</p>
                  <p className="text-xs truncate" style={{ color: colors.text.secondary }}>{trip.origin_country}</p>
                </div>
              </div>
            </div>

            <div className="flex-1 text-center px-4">
              <div className="flex items-center justify-center" style={{ color: colors.text.tertiary }}>
                <div style={{ width: '32px', height: '1px', backgroundColor: colors.border }}></div>
                <ArrowRight className="h-4 w-4 mx-2" />
                <div style={{ width: '32px', height: '1px', backgroundColor: colors.border }}></div>
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center justify-end" style={{ color: colors.text.secondary }}>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide" style={{ color: colors.text.tertiary }}>To</p>
                  <p className="font-semibold truncate" style={{ color: colors.text.primary }}>{trip.destination_city}</p>
                  <p className="text-xs truncate" style={{ color: colors.text.secondary }}>{trip.destination_country}</p>
                </div>
                <MapPin className="h-4 w-4 ml-2" style={{ color: colors.error[500] }} />
              </div>
            </div>

            <hr className="my-4" style={{ border: '0.5px broken', borderColor: colors.border }} />

          </div>
        </div>

        {/* Key metrics */}
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div className="text-center p-1 rounded-xl" style={{ backgroundColor: colors.neutral[50] }}>
            <Timer
              className="h-4 w-4 mx-auto mb-1"
              style={{ color: isDepartingSoon ? colors.error[500] : colors.warning[500] }}
            />
            <p className="text-xs" style={{ color: colors.text.tertiary }}>Departs</p>
            <p
              className="font-bold"
              style={{
                color: isDepartingSoon ? colors.error[600] : colors.text.primary
              }}
            >
              {daysUntilDeparture !== null ? `${daysUntilDeparture}d` : 'N/A'}
            </p>
          </div>

          <div className="text-center p-1 rounded-xl" style={{ backgroundColor: colors.neutral[50] }}>
            <PlaneTakeoff className="h-4 w-4 mx-auto mb-1" style={{ color: colors.success[500] }} />
            <p className="text-xs" style={{ color: colors.text.tertiary }}>
              Price/kg
            </p>
            <p className="font-bold" style={{ color: colors.text.primary }}>
              {formatValue(trip.price_per_kg || 0)}
            </p>
          </div>
        </div>
        <hr className="my-4" style={{ border: '0.5px broken', borderColor: colors.border }} />

        {/* Special badges */}
        <div className="flex flex-wrap gap-1 my-5">
          {trip.guaranteed_delivery && (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
              style={{
                backgroundColor: `${colors.warning[600]}`,
                color: colors.warning[100],
                borderColor: `${colors.warning[600]}`
              }}
            >
              <ShoppingBasket className="h-3 w-3 mr-1" />
              Guaranteed
            </span>
          )}
          {trip.purchased && (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
              style={{
                backgroundColor: `${colors.success[600]}`,
                color: colors.success[100],
                borderColor: `${colors.success[600]}`
              }}
            >
              <Shield className="h-3 w-3 mr-1" />
              Purchase
            </span>
          )}

          {(
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
              style={{
                backgroundColor: `${colors.secondary[600]}`,
                color: colors.secondary[100],
                borderColor: `${colors.secondary[600]}`
              }}
            >
              <Users2 className="h-3 w-3 mr-1" />
              {trip.applications_count || 0}
            </span>
          )}
        </div>

        {/* Action Buttons */}
        {(canEdit || canCancel || canDelete) && (
          <div className="flex gap-2 mt-4">
            {canEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onEdit) onEdit(trip);
                }}
                className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg border border-theme-primary text-theme-primary hover:bg-theme-primary hover:text-white transition-colors"
                style={{
                  borderColor: colors.primary[500],
                  color: colors.primary[600]
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.primary[500];
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = colors.primary[600];
                }}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </button>
            )}
            
            {canCancel && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onCancel) onCancel(trip);
                }}
                className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg border border-theme-secondary text-theme-secondary hover:bg-theme-error hover:text-white transition-colors"
                style={{
                  borderColor: colors.warning[500],
                  color: colors.warning[600]
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.warning[500];
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = colors.warning[600];
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </button>
            )}
            {canDelete && !canCancel && !canEdit &&(
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDelete) onDelete(trip);
                }}
                className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg border border-theme-error text-theme-error hover:bg-theme-error hover:text-white transition-colors"
                style={{
                  borderColor: colors.error[500],
                  color: colors.error[600]
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = colors.error[500];
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = colors.error[600];
                }}
              >
                <Trash className="h-4 w-4 mr-1" />
                Delete
              </button>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default TripCardSleek;
